<!--suppress ExceptionCaughtLocallyJS -->
<script>
import trim from 'lodash/trim';

import uiMixin from '@/mixins/ui-mixin';
import { base64ToArrayBuffer, formatDate, formatErrorToHtml, getLocale } from '@/helpers/';
import { euSignService } from '@/sign/services/eusign.service';
import { PrivateKeyType } from '@/sign/models/sign-constants';
import { determineKeyTypeByCertificates, isSealCertificates } from '@/sign/helpers/sign.helper';
import { logger } from '@/helpers/log.service';
import { actions as _act } from '@/models/store.actions';
import { SigningParams } from '../models/signing-params.model';
import { SHORT_DATE_FORMAT } from '@/models/defaults';
import { signService } from '@/sign/services/sign.service';
import { isCancelledError } from '@/helpers/error-helper';

const euSignUrls = {
  helpURL: 'http://iit.com.ua/download/productfiles/EUSignWebOManual.pdf',
  nativeLibraryInstallURL: 'http://iit.com.ua/download/productfiles/EUSignWebInstall.exe',
  // todo check browser type and get links
  webExtensionInstallURL:
    'https://chrome.google.com/webstore/detail/%D1%96%D1%96%D1%82-%D0%BA%D0%BE%D1%80%D0%B8%D1%81%D1%82%D1%83%D0%B2%D0%B0%D1%87-%D1%86%D1%81%D0%BA-1-%D0%B1%D1%96%D0%B1%D0%BB/jffafkigfgmjafhpkoibhfefeaebmccg?utm_source=chrome-app-launcher-info-dialog'
};

export default {
  name: 'AppSignBasePrivateKeyReader',
  mixins: [uiMixin],
  props: {
    certificateAuthority: { default: null },
    disabled: { default: false },
    eventBus: { default: null },
    privateKeyType: { default: '' },
    useUserCertificates: { default: false },
    signMethod: { default: '' },
    signProvider: { default: '' },
    shouldValidateKeyType: { type: Boolean, default: false },
    incKeyReaderType: { type: String, default: '' }
  },
  data() {
    return {
      euSignLibrary: null,
      euSignLibraryInitializedPromise: null,
      keyIsReaded: false,
      keyPassword: '',
      keyReaderType: '',
      messageIsError: true,
      messageText: '',
      warningText: '',
      endUserOwnerInfo: null,
      ownCertificates: null,
      euSignContext: null,
      privateKeyContext: null,
      useCtxEuSignFunctions: false
    };
  },
  computed: {
    componentDisabled() {
      return this.disabled || this.keyIsReaded;
    },
    isStamp() {
      return this.privateKeyType === PrivateKeyType.Seal;
    },
    currentUserEmail() {
      return this.$store.getters[_act.AUTH_GET_EMAILUSERGET] || '';
    },
    userStoredCertificates() {
      return this.$store.getters[_act.SIGN_GET_USER_CERT](this.currentUserEmail);
    },
    signingParamsRequestMsgBase() {
      return `SigningParams-Request-${this.privateKeyType}-${this.signMethod}`;
    },
    signingParamsRequestMsg() {
      return trim(`${this.signingParamsRequestMsgBase}-${this.signProvider}-${this.keyReaderType}`, '-');
    },
    signingParamsResponseMsgBase() {
      return `SigningParams-Response-${this.privateKeyType}-${this.signMethod}`;
    },
    signingParamsResponseMsg() {
      return trim(`${this.signingParamsResponseMsgBase}-${this.signProvider}-${this.keyReaderType}`, '-');
    },
    isLogin() {
      return this.$store.getters[_act.AUTH_GET_ISLOGINEDGET];
    },
    isReadyToSign() {
      return this.keyIsReaded;
    },
    isSeal() {
      return this.privateKeyType === PrivateKeyType.Seal;
    },
    serverTime() {
      return this.$store.getters[_act.SETT_GET_SERV_TIME];
    },
    daysBeforeCertExpireToNotify() {
      return (this.$store.getters[_act.SETT_GET_APPSET] || {})['notifyCertificateExpirationBeforeDays'] || 0;
    },
    showCertExpirationWarning() {
      let signSettings = signService.getCurrentSettings();
      return signSettings && signSettings.showCertExpirationWarning;
    }
  },
  created() {
    logger.debug(`[${this.$options.name}:created] signMethod="${this.signMethod}" signProvider"${this.signProvider}" privateKeyType="${this.privateKeyType}" keyReaderType="${this.keyReaderType}" shouldValidateKeyType=${this.shouldValidateKeyType} useUserCertificates=${this.useUserCertificates}`);
    this.initEuSignLibrary();
  },
  mounted() {
    this.onComponentBecameVisible();
    this.subscribe();
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  methods: {
    getEuSignLib() {
      return euSignService.getLibrary(this.signMethod, this.privateKeyType, this.signProvider, this.keyReaderType);
    },
    getUserCertificates() {
      let certs;
      if (this.useUserCertificates && this.userStoredCertificates.length > 0) {
        certs = this.userStoredCertificates.map((c) => base64ToArrayBuffer(c.content));
      }
      return certs;
    },
    handleError(error, header) {
      logger.error(error);
      this.messageIsError = true;
      this.messageText = formatErrorToHtml(error, header);
    },
    handleGetOwnCertificatesResult(certificates) {
      let keyType = determineKeyTypeByCertificates(certificates) || getLocale('appSignBasePrivateKeyReader.typeUndefine');
      return '</br>' + getLocale('appSignBasePrivateKeyReader.keyType') + keyType;
    },
    handleCertExpiration(certificates) {
      this.warningText = '';
      if (!this.showCertExpirationWarning || !certificates || !certificates.length) return;

      let certEndTime = certificates[0] && certificates[0].infoEx ? certificates[0].infoEx.certEndTime : null;
      if (certEndTime) {
        let certDateEnd = certEndTime['wYear']
          ? new Date(certEndTime['wYear'], +certEndTime['wMonth'] - 1, certEndTime['wDay'], certEndTime['wHour'], certEndTime['wMinute'], certEndTime['wSecond'])
          : new Date(certEndTime);
        let daysBeforeCertExpire = Math.floor((certDateEnd - this.serverTime) / (1000 * 60 * 60 * 24));
        if (daysBeforeCertExpire < this.daysBeforeCertExpireToNotify) {
          let dateExpired = formatDate(certDateEnd, SHORT_DATE_FORMAT);
          this.warningText = `<div>${getLocale('appSignBasePrivateKeyReader.certificateExpires', { dateExpired })}</div>`;
        }
      }
    },
    handleInitError(error) {
      if (error && (error.code === 4097 || error.code === 4098)) {
        logger.error(error);
        this.messageIsError = true;
        this.messageText = '<b>' + getLocale('appSignBasePrivateKeyReader.errorEuSignInit') + '</b></br>';
        const urls = error.libInfo || euSignUrls;
        if (urls.nativeLibraryInstallURL) {
          urls.nativeLibraryInstallURL = urls.nativeLibraryInstallURL.replace('http:', 'https:');
        }
        this.messageText += `${getLocale('appSignBasePrivateKeyReader.download1')}</br></br>`;
        this.messageText += `<a class="download-link" href="${urls.nativeLibraryInstallURL}" download>${getLocale('appSignBasePrivateKeyReader.download2')}</a></br>`;
        this.messageText += `<a class="download-link" target="_blank" href="${urls.webExtensionInstallURL}">${getLocale('appSignBasePrivateKeyReader.download3')}</a></br>`;
        this.messageText += `<a class="download-link" target="_blank" href="${urls.helpURL}">${getLocale('appSignBasePrivateKeyReader.download4')}</a></br>`;
        this.messageText += getLocale('appSignBasePrivateKeyReader.download5');
      } else {
        this.handleError(error, 'appSignBasePrivateKeyReader.errorEuSignInit');
      }
    },
    handleReadKeyError(error) {
      if (!isCancelledError(error)) {
        this.handleError(error, 'appSignBasePrivateKeyReader.errorReadKey');
        if (error && error.code === 51) {
          this.eventBus.$emit('app-sign-read-key-certificate-not-found-error');
          this.messageText += `${getLocale('appSignBasePrivateKeyReader.selectCertificatesRequired')}`;
        }
      }
    },
    handleReadKeyResult(endUserOwnerInfo) {
      let msg = `<b>${getLocale('appSignBasePrivateKeyReader.readSuccess')}</b>`;
      if (endUserOwnerInfo.issuerCN) {
        msg += `</br>${getLocale('appSignBasePrivateKeyReader.readParametersAcsk')}&nbsp;${endUserOwnerInfo.issuerCN}`;
      }
      if (endUserOwnerInfo.subjCN) {
        msg += `</br>${getLocale('appSignBasePrivateKeyReader.readParametersOwner')}&nbsp;${endUserOwnerInfo.subjCN}`;
      }
      if (endUserOwnerInfo.subjTitle) {
        msg += `</br>${getLocale('appSignBasePrivateKeyReader.readParametersType')}&nbsp;${endUserOwnerInfo.subjTitle}`;
      }
      return msg;
    },
    async initEuSignLibrary() {
      if (!this.keyReaderType && !!this.incKeyReaderType) {
        this.keyReaderType = this.incKeyReaderType;
      }

      let resolver;
      this.euSignLibraryInitializedPromise = new Promise((resolve) => (resolver = resolve));
      this.euSignLibrary = this.getEuSignLib();
      if (!this.euSignLibrary) {
        this.showLoading({}, true);
        try {
          await euSignService.init(this.signMethod, this.privateKeyType, this.signProvider, this.keyReaderType);
          this.euSignLibrary = this.getEuSignLib();
          resolver();
        } catch (error) {
          this.handleInitError(error);
        } finally {
          await this.hideLoading();
          this.setFocus();
        }
      } else {
        resolver();
        this.setFocus();
      }
    },
    readKey(certs) {},
    async readKeyClick({ signal } = {}) {
      return new Promise(async(resolve) => {
        this.showLoading({}, true);

        let cancelled = false;

        const onAbort = () => {
          cancelled = true;

          this.euSignLibrary.ResetOperationKSP();

          resolve();
        };

        try {
          if (!this.euSignLibrary) {
            throw new Error(getLocale('appSignBasePrivateKeyReader.errorNoInit').toString());
          }

          if (signal) {
            signal.addEventListener('abort', onAbort, { once: true });
          }

          if (!this.euSignContext && this.useCtxEuSignFunctions) {
            this.euSignContext = await this.euSignLibrary.CtxCreate();
          }

          let certs = this.getUserCertificates();
          if (this.useCtxEuSignFunctions) {
            this.privateKeyContext = await this.readKey(certs);
            this.endUserOwnerInfo = this.privateKeyContext.ownerInfo;
          } else {
            const endUserOwnerInfo = await this.readKey(certs);
            if (cancelled) return;
            this.endUserOwnerInfo = endUserOwnerInfo;
          }

          this.messageIsError = false;
          this.messageText = this.handleReadKeyResult(this.endUserOwnerInfo);

          this.ownCertificates = await this.getOwnCertificates();

          this.validateKeyType(this.ownCertificates);

          this.messageText += this.handleGetOwnCertificatesResult(this.ownCertificates);

          this.handleCertExpiration(this.ownCertificates);

          this.keyIsReaded = true;
          this.$emit('keyReaded', { endUserOwnerInfo: this.endUserOwnerInfo, cert: this.ownCertificates });
        } catch (error) {
          if (cancelled) {
            logger.error(error);
          } else {
            this.handleReadKeyError(error);
          }
        } finally {
          if (signal) {
            signal.removeEventListener('abort', onAbort);
          }
          await this.hideLoading();

          resolve();
        }
      });
    },
    getHtmlElementToFocus() {},
    async getOwnCertificates() {
      return this.useCtxEuSignFunctions
        ? this.euSignLibrary.CtxGetOwnCertificates(this.privateKeyContext)
        : this.euSignLibrary.GetOwnCertificates();
    },
    async reset() {
      await this.resetBase();
    },
    async resetBase() {
      this.keyIsReaded = false;
      this.keyPassword = '';
      this.endUserOwnerInfo = null;
      this.ownCertificates = null;
      if (this.euSignLibrary) {
        this.messageIsError = false;
        this.messageText = '';
        this.warningText = '';
        await this.resetPrivateKey();
      }
      this.privateKeyContext = null;
    },
    resetPrivateKey() {
      return this.useCtxEuSignFunctions
        ? this.privateKeyContext
          ? this.euSignLibrary.CtxFreePrivateKey(this.privateKeyContext)
          : Promise.resolve()
        : this.euSignLibrary.ResetPrivateKey();
    },
    setFocus() {
      this.$nextTick(() => {
        let el = this.getHtmlElementToFocus();
        if (el && 'focus' in el) {
          el.focus();
        }
      });
    },
    validateKeyType(certificates) {
      if (this.shouldValidateKeyType) {
        if (this.isStamp) {
          let exists = isSealCertificates(certificates);
          if (!exists) {
            throw new Error(getLocale('appSignBasePrivateKeyReader.stampWrongType').toString());
          }
        } else if (!this.isStamp) {
          let exists = isSealCertificates(certificates);
          if (exists) {
            throw new Error(getLocale('appSignBasePrivateKeyReader.signWrongType').toString());
          }
        }
      }
    },
    subscribe() {
      this.eventBus.$on('app-sign-reset-private-key', this.reset);
      this.eventBus.$on(this.signingParamsRequestMsg, this.onSigningParamsRequest);
      this.eventBus.$on('app-sign-method-changed', this.onSignMethodChanged);
      this.eventBus.$on('app-sign-destroy', this.onDestroyed);
      this.eventBus.$on('app-sign-prepare', this.onPrepareComponent);
    },
    unsubscribe() {
      this.eventBus.$off('app-sign-reset-private-key', this.reset);
      this.eventBus.$off(this.signingParamsRequestMsg, this.onSigningParamsRequest);
      this.eventBus.$off('app-sign-method-changed', this.onSignMethodChanged);
      this.eventBus.$off('app-sign-destroy', this.onDestroyed);
      this.eventBus.$off('app-sign-prepare', this.onPrepareComponent);
    },
    async onSigningParamsRequest() {
      let prms = null;
      try {
        if (!this.isReadyToSign) {
          await this.tryToReadKey();
        }
        if (this.isReadyToSign) {
          prms = await this.getSigningParams();
          prms = await this.appendAdditionalSigningParams(prms);
        }
      } catch {
        // ignored
      }
      this.eventBus.$emit(this.signingParamsResponseMsg, prms);
    },
    async getSigningParams() {
      return new SigningParams({
        signMethod: this.signMethod,
        privateKeyType: this.privateKeyType,
        signProvider: this.signProvider,
        keyReaderType: this.keyReaderType,
        data: {
          endUserOwnerInfo: this.endUserOwnerInfo,
          ownCertificates: this.ownCertificates,
          privateKeyContext: this.useCtxEuSignFunctions ? this.privateKeyContext : undefined
        }
      });
    },
    appendAdditionalSigningParams(prms) {
      return Promise.resolve(prms);
    },
    onSignMethodChanged(prms) {
      if (prms.signMethod === this.signMethod &&
          (!this.signProvider || prms.signProvider === this.signProvider) &&
          (!this.keyReaderType || prms.keyReaderType === this.keyReaderType)) {
        this.onComponentBecameVisible();
      }
    },
    onComponentBecameVisible() {
      this.eventBus.$emit('app-sign-change-ui-settings', {
        isResetBtnVisible: true
      });
      if (this.keyIsReaded) {
        this.$emit('ready');
      } else {
        this.$emit('not-ready');
      }
    },
    onDestroyed() {
      if (this.euSignLibrary && this.useCtxEuSignFunctions) {
        this.euSignLibrary.CtxFreePrivateKey(this.privateKeyContext);
        this.euSignLibrary.CtxFree(this.euSignContext);
      }
    },
    onResetBtnClicked() {
      this.eventBus.$emit('app-sign-reset-private-key-from-child');
    },
    async tryToReadKey() {
      return Promise.resolve();
    },
    onPrepareComponent() {},
    async onShouldValidateKeyTypeChanged(newVal, oldVal) {
      if (newVal && newVal !== oldVal && this.keyIsReaded) {
        try {
          this.validateKeyType(this.ownCertificates);
        } catch (error) {
          this.keyIsReaded = false;
          this.endUserOwnerInfo = null;
          this.ownCertificates = null;
          if (this.euSignLibrary) {
            await this.resetPrivateKey();
          }
          this.$emit('not-ready');
          this.handleReadKeyError(error);
        }
      }
    }
  },
  watch: {
    shouldValidateKeyType(newVal, oldVal) {
      this.onShouldValidateKeyTypeChanged(newVal, oldVal);
    }
  }
};
</script>
